import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";

export const useConfiguracaoObrasViewModel = () => {
  const {
    obras: { getAllAtributosObrasCadastroUseCase, updateAtributosObrasCadastroUseCase },
  } = useDependencyInjection();
  const [IdAtributosObras, setIdAtributosObras] = useState<number>();
  const { pushInfoNotification, pushErrorNotification } = usePageLoader();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [config, setConfig] = useState({
    exibirDataCadastroChecklist: null as boolean | null,
    exibirDataCadastroInspecao: null as boolean | null,
    exibirCampoAprovacaoFinal: null as boolean | null,
  });

  const [saving, setSaving] = useState(false);

  usePageStartBootstrap(async () => {
    try {
      await loadConfiguracoesObras();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });

  // 🔹 Carrega as configurações da API
  const loadConfiguracoesObras = useCallback(async () => {
    try {
      const data = await getAllAtributosObrasCadastroUseCase.getAllAtributosObrasCadastro();
      setIdAtributosObras(data.idAtributosObras);
      setConfig({
        exibirDataCadastroChecklist: data.exibirDataChecklist,
        exibirDataCadastroInspecao: data.exibirDataInspecao,
        exibirCampoAprovacaoFinal: data.exibirDataAprovacaoFinal,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar configurações de obras!"));
    }
  }, [
    getAllAtributosObrasCadastroUseCase,
    setIdAtributosObras,
    setConfig,
    pushErrorNotification,
  ]);

  // 🔹 Salva as configurações via API
  const handleSalvarConfiguracoes = useCallback(async () => {
    try {
      setSaving(true);
      if (IdAtributosObras === undefined) {
        throw new Error("IdAtributosObras não definido!");
      }

      await updateAtributosObrasCadastroUseCase.updateAtributosObrasCadastro(
        IdAtributosObras,
        config.exibirDataCadastroChecklist,
        config.exibirDataCadastroInspecao,
        config.exibirCampoAprovacaoFinal
      );

      await loadConfiguracoesObras(); // recarrega após salvar
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar configurações de obras!"));
    } finally {
      setSaving(false);
    }
  }, [
    IdAtributosObras,
    updateAtributosObrasCadastroUseCase,
    config.exibirCampoAprovacaoFinal,
    config.exibirDataCadastroChecklist,
    config.exibirDataCadastroInspecao,
    loadConfiguracoesObras,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  // 🔹 Setters individuais (mantidos para os toggles)
  const setExibirDataCadastroChecklist = (value: boolean) => setConfig((prev) => ({ ...prev, exibirDataCadastroChecklist: value }));

  const setExibirDataCadastroInspecao = (value: boolean) => setConfig((prev) => ({ ...prev, exibirDataCadastroInspecao: value }));

  const setExibirCampoAprovacaoFinal = (value: boolean) => setConfig((prev) => ({ ...prev, exibirCampoAprovacaoFinal: value }));

  const salvarConfiguracoes = useCallback(async () => {
    await handleSalvarConfiguracoes();
  }, [handleSalvarConfiguracoes]);

  const recarregarConfiguracoes = useCallback(async () => {
    await loadConfiguracoesObras();
  }, [loadConfiguracoesObras]);

  return {
    config,
    saving,
    loadConfiguracoesObras,
    handleSalvarConfiguracoes,
    setExibirDataCadastroChecklist,
    setExibirDataCadastroInspecao,
    setExibirCampoAprovacaoFinal,
    salvarConfiguracoes,
    recarregarConfiguracoes,
  };
};
